import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { ProductCard } from "../components/ProductCard";
import { ArrowDown, Download, ShieldCheck, Zap } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Storefront() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const featured = products.find((p) => p.featured) || products[0];

  return (
    <div className="App relative">
      <div className="vm-grain" />
      <Navbar />

      {!category && (
        <section className="relative overflow-hidden border-b border-white/10">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-[#09090b]" />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
            <p className="text-xs tracking-[0.25em] uppercase text-zinc-400 mb-6 vm-fade-up">Digital products · Instant delivery</p>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light max-w-3xl leading-[1.05] vm-fade-up" style={{ animationDelay: "80ms" }}>
              Premium e-books, templates & courses to <span className="italic">level up</span> your craft.
            </h1>
            <p className="text-zinc-300 mt-6 max-w-xl text-base sm:text-lg font-light vm-fade-up" style={{ animationDelay: "160ms" }}>
              Buy once, download instantly. Built for founders, designers and creators who ship.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 vm-fade-up" style={{ animationDelay: "240ms" }}>
              <a href="#catalog" data-testid="browse-cta" className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-md font-medium hover:bg-zinc-200 transition-colors">
                Browse the catalog <ArrowDown className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-14 flex flex-wrap gap-8 text-sm text-zinc-400 vm-fade-up" style={{ animationDelay: "320ms" }}>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-white" /> Instant download</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-white" /> Secure Stripe checkout</span>
              <span className="flex items-center gap-2"><Download className="w-4 h-4 text-white" /> Lifetime access</span>
            </div>
          </div>
        </section>
      )}

      <section id="catalog" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 relative z-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">{category ? "Category" : "The catalog"}</p>
            <h2 className="font-head text-2xl sm:text-3xl tracking-tight capitalize">{category || "All products"}</h2>
          </div>
          <span className="text-sm text-zinc-500 font-mono">{products.length} items</span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="rounded-xl border border-white/10 aspect-[4/3] bg-zinc-900/50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-white/10 py-10 text-center text-sm text-zinc-500 relative z-10">
        Digital Dime Lab — Digital goods, delivered instantly.
      </footer>
    </div>
  );
}
